import { useCallback } from 'react';

import { useTranslate } from 'src/locales';

import { toast } from 'src/components/snackbar';

/**
 * Options for error handling behavior
 */
interface ErrorHandlerOptions {
  /** If true, don't show toast notification */
  silent?: boolean;
  /** Custom error message to display */
  customMessage?: string;
  /** Callback to execute after error handling */
  onError?: (error: any) => void;
}

/**
 * Custom hook for standardized billing error handling.
 * Provides consistent error messages, logging, and user feedback across the billing module.
 *
 * @returns Object with error handling functions
 *
 * @example
 * ```typescript
 * const { handleError } = useBillingErrorHandler();
 *
 * try {
 *   await fetchData();
 * } catch (error) {
 *   handleError(error, 'Fetching billing data');
 * }
 * ```
 */
export function useBillingErrorHandler() {
  const { t } = useTranslate('translation');

  /**
   * Main error handler function.
   * Determines error type and shows appropriate user-friendly message.
   *
   * @param error - Error object from API or other source
   * @param context - Description of what operation failed
   * @param options - Additional options for error handling
   */
  const handleError = useCallback(
    (error: any, context: string, options?: ErrorHandlerOptions) => {
      // Log error for debugging
      console.error(`❌ ${context}:`, error);

      // Silent mode - just log, don't show toast
      if (options?.silent) {
        options?.onError?.(error);
        return;
      }

      let errorMessage = options?.customMessage;

      // Determine error type and set appropriate message
      if (!errorMessage) {
        if (error?.status === 401 || error?.originalStatus === 401) {
          errorMessage = t('SESSION_EXPIRED');
          // Optional: Trigger logout or redirect to login
          // router.push('/auth/login');
        } else if (error?.status === 403 || error?.originalStatus === 403) {
          errorMessage = t('INSUFFICIENT_PERMISSIONS');
        } else if (error?.status === 404 || error?.originalStatus === 404) {
          errorMessage = t('RESOURCE_NOT_FOUND');
        } else if (error?.status === 422 || error?.originalStatus === 422) {
          // Validation error - try to get specific message from API
          errorMessage = error?.data?.message || t('VALIDATION_ERROR');
        } else if (error?.status === 429 || error?.originalStatus === 429) {
          errorMessage = t('TOO_MANY_REQUESTS');
        } else if (error?.status >= 500 || error?.originalStatus >= 500) {
          // Show specific message if present
          if (
            error?.message === 'No active subscription found for this tenant' ||
            error?.data?.message === 'No active subscription found for this tenant'
          ) {
            errorMessage = 'No active subscription found for this tenant';
          } else {
            errorMessage = error?.data?.message || t('SOMETHING_WENT_WRONG_TRY_AGAIN');
          }
        } else if (error?.name === 'AbortError') {
          // Request was cancelled - usually intentional, don't show error
          return;
        } else if (error?.message) {
          // Use error message if available
          errorMessage = error.message;
        } else {
          // Generic fallback
          errorMessage = t('GENERIC_ERROR_MESSAGE');
        }
      }

      // Show toast notification
      toast.error(errorMessage);

      // Execute callback if provided
      options?.onError?.(error);

      // Optional: Log to error tracking service
      // if (typeof window !== 'undefined' && window.Sentry) {
      //   window.Sentry.captureException(error, {
      //     tags: { context, module: 'billing' },
      //   });
      // }
    },
    [t]
  );

  /**
   * Handles payment-specific errors with specialized messages.
   *
   * @param error - Payment error object
   * @param context - Payment operation context
   */
  const handlePaymentError = useCallback(
    (error: any, context: string = 'Payment processing') => {
      console.error(`❌ ${context}:`, error);

      let errorMessage: string;

      // Payment-specific error handling
      if (error?.code === 'card_declined') {
        errorMessage = t('CARD_DECLINED');
      } else if (error?.code === 'insufficient_funds') {
        errorMessage = t('INSUFFICIENT_FUNDS');
      } else if (error?.code === 'invalid_card') {
        errorMessage = t('INVALID_CARD');
      } else if (error?.code === 'expired_card') {
        errorMessage = t('CARD_EXPIRED');
      } else if (error?.code === '3ds_failed') {
        errorMessage = t('3DS_AUTHENTICATION_FAILED');
      } else if (error?.status === 402 || error?.message === 'BLOCKED: CARD COUNTRY NOT ALLOWED') {
        errorMessage = error?.message === 'BLOCKED: CARD COUNTRY NOT ALLOWED' 
          ? t('CARD_COUNTRY_NOT_ALLOWED') 
          : t('PAYMENT_REQUIRED');
      } else if (error?.message === 'You have other unpaid invoices, please pay those first' || error?.data?.message === 'You have other unpaid invoices, please pay those first') {
        errorMessage = t('UNPAID_INVOICES');
      } else {
        errorMessage = t('PAYMENT_FAILED');
      }

      toast.error(errorMessage);

      return errorMessage;
    },
    [t]
  );

  /**
   * Handles API validation errors with field-specific messages.
   *
   * @param error - Validation error object
   * @returns Array of field-specific error messages
   */
  const handleValidationError = useCallback(
    (error: any): string[] => {
      const errors: string[] = [];

      if (error?.data?.errors && typeof error.data.errors === 'object') {
        // Handle field-specific validation errors
        Object.entries(error.data.errors).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            messages.forEach((msg) => {
              errors.push(`${field}: ${msg}`);
              toast.error(`${field}: ${msg}`);
            });
          } else if (typeof messages === 'string') {
            errors.push(`${field}: ${messages}`);
            toast.error(`${field}: ${messages}`);
          }
        });
      } else if (error?.data?.message) {
        // Single validation message
        errors.push(error.data.message);
        toast.error(error.data.message);
      } else {
        // Generic validation error
        const message = t('VALIDATION_ERROR');
        errors.push(message);
        toast.error(message);
      }

      return errors;
    },
    [t]
  );

  /**
   * Handles network errors (offline, timeout, etc.)
   *
   * @param error - Network error object
   */
  const handleNetworkError = useCallback(
    (error: any) => {
      console.error('❌ Network error:', error);

      let errorMessage: string;

      if (!navigator.onLine) {
        errorMessage = t('NETWORK_OFFLINE');
      } else if (error?.name === 'TimeoutError') {
        errorMessage = t('REQUEST_TIMEOUT');
      } else {
        errorMessage = t('NETWORK_ERROR');
      }

      toast.error(errorMessage);
    },
    [t]
  );

  return {
    handleError,
    handlePaymentError,
    handleValidationError,
    handleNetworkError,
  };
}
